import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as React from 'react';
import { useAuth } from 'react-oidc-context';
import { LinkContainer } from 'react-router-bootstrap';
import { useParams } from 'react-router-dom';
import API, { IPatchRequest } from '../../common/API';
import { AddLinkedServerResponse, LinkedServer, LinkedServerTenant, TenantLinkStatus } from '../../common/LinkedServer';
import { FindResponse } from '../../common/Type';

export function LinkedServerEditComponent() {
    const auth = useAuth();
    const { serverId } = useParams();

    const [section, setSection] = React.useState<string>("general")
    const [server, setLinkedServer] = React.useState<LinkedServer>();
    const [name, setName] = React.useState<string>();
    const [linkKey, setLinkKey] = React.useState<string>();
    const [linkKeyExpiry, setLinkKeyExpiry] = React.useState<Date>()
    const [isExpired, setIsExpired] = React.useState < boolean>() // Indicates whether link key has expired


    React.useEffect(() => {
        const getLinkedServer = async () => {
            const response: FindResponse<LinkedServer> | null = await API.get("/api/v1/LinkedServer/" + serverId, auth);

            if (response?.result === null)
                return;

            setLinkedServer(response?.result);
            setName(response?.result.name);

            setLinkKey(response?.result.linkKey)
            if (response?.result.linkKey !== "") {
                const expiryDate = new Date(response?.result.linkKeyExpiry + "Z")
                setLinkKeyExpiry(expiryDate)
                setIsExpired(expiryDate.getTime() < (new Date().getTime()))
            }
        }

        getLinkedServer();
    }, [auth, serverId])


    return (
        <div>
            <div className="d-flex justify-content-between mb-3 align-items-middle">
                <h1 className="header-text d-flex align-items-center">
                    <FontAwesomeIcon icon="server" className="fs-3 me-2" />
                    <LinkContainer to={"/linkedServers"}>
                        <a>
                            Linked Servers
                        </a>
                    </LinkContainer>

                    <FontAwesomeIcon icon="arrow-right" className="fs-3 mx-2" />
                    <span>{name}</span>
                </h1>
            </div>

            {server != null && linkKey === "" && renderLinked()}
            {server != null && linkKey !== "" && renderUnlinked()}            
        </div>
    )

    function renderUnlinked() {
        const regenerate = async () => {
            const response: AddLinkedServerResponse | null = await API.put("/api/v1/LinkedServer/" + serverId + "/Relink", {}, auth);

            if (response == null)
                return;

            setLinkKey(response.linkKey)
            setLinkKeyExpiry(new Date(response?.linkExpiry))
            setIsExpired(false)
        }

        const copyLinkKey = () => {
            navigator.clipboard.writeText(linkKey ?? "")
        }

        return (
            <div className="row">
                
                <div className="col-12 col-xl-6">
                    <div className="card">
                        <div className="card-header">
                            <h5 className="card-title mb-0">Link Server</h5>
                        </div>
                        <div className="card-body">
                        <p>Enter the <strong>Link Key</strong> below into the software to proceed.</p>
                            <div className="form-group row">
                                <label className="col-form-label col-lg-2">Link Key</label>
                                <div className="col-lg-8">
                                    <div className="input-group mb-2">
                                        <input type="text" disabled className="form-control" value={linkKey} />
                                        <button type="button" className="btn btn-primary" onClick={(e) => { copyLinkKey() } }>
                                            <FontAwesomeIcon icon="copy" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                            <div className="form-group row">
                                <label className="col-form-label col-lg-2">Expiration</label>
                                <div className="col-lg-8">
                                    <p className={"form-control-static " + (isExpired ? "text-danger" : "")}>{linkKeyExpiry?.toLocaleString()}</p>
                                </div>
                            </div>
                            <button type="button" className="btn w-100 btn-primary" disabled={!isExpired} onClick={(e) => { regenerate() }}>Regenerate Link Key</button>
                        </div>
                    </div>
                </div>
                <div className="col-12 col-xl-6">
                    <div className="tab">
                        <ul className="nav nav-tabs">
                            <li className="nav-item">
                                <a className="nav-link active" data-bs-toggle="tab" href="#nonMultiTenant" role="tab">Non Multi-Tenant</a>
                            </li>
                            <li className="nav-item">
                                <a className="nav-link active" data-bs-toggle="tab" href="#multiTenant" role="tab">Multi-Tenant</a>
                            </li>
                        </ul>
                        <div className="tab-content">
                            <div className="tab-pane active" id="nonMultiTenant" role="tabpanel">
                                <h4 className="tab-title">Non-Multi Tenant</h4>
                                <p>Instructions to link a server that is hosting a single customer.</p>
                                <ol>
                                    <li>Login to the software with a System Admin account.</li>
                                    <li>Browse to the <strong>System Settings</strong> page.</li>
                                    <li>Open the <strong>Server Link</strong> section.</li>
                                    <li>Copy the <strong>Link Key</strong> and paste it in the provided field.</li>
                                    <li>Click <strong>Save</strong>.</li>
                                </ol>
                            </div>
                            <div className="tab-pane" id="multiTenant" role="tabpanel">
                                <h4 className="tab-title">Multi Tenant</h4>
                                <p>Instructions to link a server that is hosting multiple customers.</p>
                                <ol>
                                    <li>Login to System Manager with a System Admin account.</li>
                                    <li>Browse to the <strong>Settings</strong> page.</li>
                                    <li>Open the <strong>Server Link</strong> section.</li>
                                    <li>Copy the <strong>Link Key</strong> and paste it in the provided field.</li>
                                    <li>Click <strong>Save</strong>.</li>
                                </ol>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    function renderLinked() {
        return (
            <div className="row">
                <div className="col-12 col-lg-4 col-xxl-2">
                    <div className="card">
                        <div className="card-body">
                            <ul className="nav flex-column">
                                <li className="nav-item">
                                    <a className="nav-link px-2 d-flex align-items-center text-reset" onClick={(e) => setSection('general')}>
                                        <FontAwesomeIcon icon="server" className="me-2 fa-fw" />
                                        <span className={section === "general" ? "font-weight-bold" : ""}>General</span>
                                    </a>
                                </li>
                                <li className="nav-item">
                                    <a className="nav-link px-2 d-flex align-items-center text-reset" onClick={(e) => setSection('tenants')}>
                                        <FontAwesomeIcon icon="building" className="me-2 fa-fw" />
                                        <span className={section === "tenants" ? "font-weight-bold" : ""}>Tenants</span>
                                    </a>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
                {section === "general" && renderGeneralSection()}
                {section === "tenants" && renderTenantSection()}
            </div>
        )
    }

    function renderGeneralSection() {
        const update = async () => {

            let request: IPatchRequest = {
                properties: [
                    { "name": "Name", "value": name }
                ]
            }

            await API.put("/api/v1/LinkedServer/" + serverId, request, auth);
        }

        return (
            <>
                <div className="col-12 col-lg-6 col-xxl-6">
                    <div className="card">
                        <div className="card-header">
                            <h5 className="card-title mb-0">General</h5>
                        </div>
                        <div className="card-body">
                            <div className="form-group row">
                                <label className="col-form-label col-lg-2">Name</label>
                                <div className="col-lg-8">
                                    <input type="text" className="form-control" value={name} onChange={(e) => setName(e.target.value)} />
                                </div>
                            </div>
                            <button type="button" className="btn btn-primary float-end" onClick={() => update()}>Save</button>
                        </div>
                    </div>
                </div>
                <div className="col-12 col-lg-4 col-xxl-4">
                    <div className="card">
                        <div className="card-header">
                            <h6 className="card-title mb-0">Information</h6>
                        </div>
                        <div className="card-body">
                            <dl className="row">
                                <dt className="col-5 col-xxl-4">Software</dt>
                                <dd className="col-7 col-xxl-8">{server?.softwareType}</dd>
                                <dt className="col-5 col-xxl-4">Software Version</dt>
                                <dd className="col-7 col-xxl-8">{server?.lastVersion}</dd>
                                <dt className="col-5 col-xxl-4">Multi Tenant</dt>
                                <dd className="col-7 col-xxl-8">{server?.multiTenant ? "Yes" : "No"}</dd>
                            </dl>
                            <hr />
                            <dl className="row">
                                <dt className="col-5 col-xxl-4">Created</dt>
                                <dd className="col-7 col-xxl-8">{server?.createdOn != null ? new Date(server.createdOn + "Z").toLocaleString() : ""}</dd>
                            </dl>
                            <hr />
                            <dl className="row">
                                <dt className="col-5 col-xxl-4">Last Connection</dt>
                                <dd className="col-7 col-xxl-8">{server?.lastConnection != null ? new Date(server.lastConnection + "Z").toLocaleString() : ""}</dd>
                                <dt className="col-5 col-xxl-4">Last Connection IP</dt>
                                <dd className="col-7 col-xxl-8">{server?.lastConnectionIP}</dd>
                            </dl>
                        </div>
                    </div>
                </div>
            </>
        )
    }

    function renderTenantSection() {
        return (
            <div className="col-10">
                <div className="card">
                    <div className="card-header">
                        <h5 className="card-title mb-0">Tenants</h5>
                    </div>
                    <div className="card-body">
                        <div className="table-responsive">
                            <table className="table">
                                <thead>
                                    <tr>
                                        <th style={{ width: '1%' }}></th>
                                        <th>Deployment</th>
                                        <th className="text-center">Account #</th>
                                        <th className="text-center">Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {server?.tenants.map((tenant: LinkedServerTenant) => {
                                        return (
                                            <tr key={tenant.id}>
                                                {tenant.status === TenantLinkStatus.New && <td>
                                                    <LinkContainer to={"/linkedServers/link/" + tenant.id}>
                                                        <button className="btn btn-sm btn-primary">
                                                            <FontAwesomeIcon icon="plus" />
                                                        </button>
                                                    </LinkContainer>
                                                </td>}
                                                {tenant.status === TenantLinkStatus.Mapped && <td>
                                                    <LinkContainer to={"/deployment/" + tenant.deploymentId}>
                                                        <button className="btn btn-sm btn-primary">
                                                            <FontAwesomeIcon icon="edit" />
                                                        </button>
                                                    </LinkContainer>
                                                </td>}
                                                <td>{tenant.name}</td>
                                                <td className="text-center">{tenant.accountNumber}</td>
                                                <td className="text-center">{TenantLinkStatus[tenant.status]}</td>
                                            </tr>)
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        )
    }
}